import { useState } from 'react';
import { Plus, Search, LayoutGrid, List, MoreHorizontal, Edit, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { cn, getStatusTextColor } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useInventory, WorkOrder } from '@/context/InventoryContext';
import { SaveIndicator } from '@/components/ui/save-indicator';

const statusColumns = [
  { key: 'in_progress', label: 'In Progress', color: 'bg-yellow-500' },
  { key: 'completed', label: 'Completed', color: 'bg-green-500' },
];

const paymentStatusConfig: Record<string, { label: string; className: string }> = {
  not_received: { label: 'Not Received', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  received: { label: 'Received', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
};

const productOptions = [
  'Sensor Box Assembly',
  'Control Box Assembly',
  'Thermalink 2.4',
  'Thermalink 5.0',
];

export function ProductionPage() {
  const { workOrders, setWorkOrders, saveStatus, updateWorkOrder, createWorkOrder, deleteWorkOrder } = useInventory();
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<WorkOrder | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<WorkOrder | null>(null);
  const [dateSortOrder, setDateSortOrder] = useState<'desc' | 'asc'>('desc'); // desc = newest first

  // Form state
  const [formProduct, setFormProduct] = useState('');
  const [formQty, setFormQty] = useState(1);
  const [formPricePerUnit, setFormPricePerUnit] = useState(0);
  const [formPoNumber, setFormPoNumber] = useState('');
  const [formOrderedDate, setFormOrderedDate] = useState('');
  const [formTargetEnd, setFormTargetEnd] = useState('');
  const [formStatus, setFormStatus] = useState('in_progress');

  // Calculate totals
  const subtotal = formQty * formPricePerUnit;
  const vat = subtotal * 0.18;
  const total = subtotal + vat;

  const filteredOrders = workOrders
    .filter(
      (wo) =>
        wo.woNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        wo.product.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const dateA = a.orderedDate ? new Date(a.orderedDate).getTime() : 0;
      const dateB = b.orderedDate ? new Date(b.orderedDate).getTime() : 0;
      return dateSortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

  // Generate next WO number
  const generateWoNumber = () => {
    const year = new Date().getFullYear();
    const nextNum = workOrders.length + 1;
    return `WO-${year}-${String(nextNum).padStart(4, '0')}`;
  };

  // Reset form
  const resetForm = () => {
    setFormProduct('');
    setFormQty(1);
    setFormPricePerUnit(0);
    setFormPoNumber('');
    setFormOrderedDate('');
    setFormTargetEnd('');
    setFormStatus('in_progress');
    setEditingOrder(null);
  };

  // Open dialog for new work order
  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  // Open dialog for editing
  const handleOpenEdit = (wo: WorkOrder) => {
    setEditingOrder(wo);
    setFormProduct(wo.product);
    setFormQty(wo.qty);
    setFormPricePerUnit(wo.pricePerUnit);
    setFormPoNumber(wo.poNumber);
    setFormOrderedDate(wo.orderedDate);
    setFormTargetEnd(wo.targetEnd);
    setFormStatus(wo.status);
    setDialogOpen(true);
  };

  // Handle create or update work order
  const handleSubmitWorkOrder = async () => {
    if (!formProduct || !formTargetEnd || formQty < 1) return;

    if (editingOrder) {
      // Update existing - use instant-save API
      const updates = {
        poNumber: formPoNumber,
        product: formProduct,
        qty: formQty,
        pricePerUnit: formPricePerUnit,
        subtotal: subtotal,
        vat: vat,
        total: total,
        status: formStatus,
        orderedDate: formOrderedDate,
        targetEnd: formTargetEnd,
        progress: formStatus === 'completed' ? 100 : 50,
      };

      // Optimistic update for UI responsiveness
      setWorkOrders(prev => prev.map(wo =>
        wo.id === editingOrder.id ? { ...wo, ...updates } : wo
      ));

      // Full update via API (converts to snake_case)
      updateWorkOrder(editingOrder.id, {
        ...updates,
      });
    } else {
      // Create new - use instant-save API
      const newWorkOrder = {
        woNumber: generateWoNumber(),
        poNumber: formPoNumber,
        product: formProduct,
        qty: formQty,
        pricePerUnit: formPricePerUnit,
        subtotal: subtotal,
        vat: vat,
        total: total,
        status: formStatus,
        payment_status: 'not_received' as const,
        orderedDate: formOrderedDate,
        targetEnd: formTargetEnd,
        progress: formStatus === 'completed' ? 100 : 50,
      };

      console.log('[ProductionPage] Creating work order:', newWorkOrder);
      const result = await createWorkOrder(newWorkOrder);
      console.log('[ProductionPage] Create result:', result);
    }

    resetForm();
    setDialogOpen(false);
  };

  // Handle delete - use instant-save API
  const handleConfirmDelete = async () => {
    if (orderToDelete) {
      await deleteWorkOrder(orderToDelete.id);
      setOrderToDelete(null);
    }
    setDeleteDialogOpen(false);
  };

  const handleOpenDelete = (wo: WorkOrder) => {
    setOrderToDelete(wo);
    setDeleteDialogOpen(true);
  };

  // Handle status change - instant save
  const handleStatusChange = (woId: number, newStatus: string) => {
    const progress = newStatus === 'completed' ? 100 : 50;
    updateWorkOrder(woId, { status: newStatus, progress });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold">Work Order</h2>
            <SaveIndicator status={saveStatus} />
          </div>
          <p className="text-muted-foreground">
            Manage work orders and track production progress
          </p>
        </div>
        <Button onClick={handleOpenCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Create Work Order
        </Button>
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search work orders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-1 rounded-md border p-1">
          <button
            onClick={() => setViewMode('kanban')}
            className={cn(
              'p-2 rounded transition-colors',
              viewMode === 'kanban' ? 'bg-muted' : 'hover:bg-muted'
            )}
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={cn(
              'p-2 rounded transition-colors',
              viewMode === 'list' ? 'bg-muted' : 'hover:bg-muted'
            )}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Empty State */}
      {workOrders.length === 0 && (
        <div className="text-center py-12 border rounded-lg bg-muted/20">
          <p className="text-muted-foreground mb-4">No work orders yet</p>
          <Button onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Work Order
          </Button>
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && workOrders.length > 0 && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {statusColumns.map((column) => (
            <div key={column.key} className="flex-shrink-0 w-72">
              <div className="flex items-center gap-2 mb-3">
                <div className={cn('h-3 w-3 rounded-full', column.color)} />
                <h3 className="font-medium">{column.label}</h3>
                <span className="text-sm text-muted-foreground">
                  ({filteredOrders.filter((wo) => wo.status === column.key).length})
                </span>
              </div>
              <div className="space-y-3 min-h-[100px]">
                {filteredOrders
                  .filter((wo) => wo.status === column.key)
                  .map((wo) => (
                    <div
                      key={wo.id}
                      className="rounded-lg border bg-card p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-mono text-primary">
                          {wo.poNumber || '-'}
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-6 w-6">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenEdit(wo)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleOpenDelete(wo)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <p className="font-medium mb-2">{wo.product}</p>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>Qty: {wo.qty}</span>
                        <span>Ordered: {wo.orderedDate || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <Select
                          value={wo.status}
                          onValueChange={(value) => handleStatusChange(wo.id, value)}
                        >
                          <SelectTrigger className="w-28 h-6 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {statusColumns.map((s) => (
                              <SelectItem key={s.key} value={s.key}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span>Due: {wo.targetEnd}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-muted-foreground">₪{wo.pricePerUnit}/unit</span>
                        <span className="font-semibold text-primary">₪{wo.total.toFixed(2)}</span>
                      </div>
                      {/* Progress bar */}
                      <div className="mt-3 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className={cn('h-full rounded-full', column.color)}
                          style={{ width: `${wo.progress}%` }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && workOrders.length > 0 && (
        <div className="rounded-lg border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left text-sm font-medium">PO Number</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Product</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Qty</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Price/Unit</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Total</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Payment</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    <button
                      onClick={() => setDateSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                      className="flex items-center gap-1 hover:text-primary transition-colors"
                    >
                      Ordered Date
                      {dateSortOrder === 'desc' ? (
                        <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUp className="h-4 w-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Target End</th>
                  <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">
                      No work orders found
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((wo) => (
                    <tr key={wo.id} className="border-b hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm font-mono">{wo.poNumber || '-'}</td>
                      <td className="px-4 py-3 text-sm font-medium">{wo.product}</td>
                      <td className="px-4 py-3 text-sm text-right">{wo.qty}</td>
                      <td className="px-4 py-3 text-sm text-right">₪{wo.pricePerUnit.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">₪{wo.total.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm">
                        <Select
                          value={wo.status}
                          onValueChange={(value) => handleStatusChange(wo.id, value)}
                        >
                          <SelectTrigger className="w-32 h-8">
                            <span className={cn(
                              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                              getStatusTextColor(wo.status)
                            )}>
                              {wo.status.replace('_', ' ')}
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            {statusColumns.map((s) => (
                              <SelectItem key={s.key} value={s.key}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Select
                          value={wo.payment_status || 'not_received'}
                          onValueChange={(value: 'not_received' | 'received') => {
                            // Instant save
                            updateWorkOrder(wo.id, { payment_status: value });
                          }}
                        >
                          <SelectTrigger className={`w-[120px] h-7 text-xs font-medium border-0 ${paymentStatusConfig[wo.payment_status || 'not_received']?.className || paymentStatusConfig.not_received.className}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="not_received">Not Received</SelectItem>
                            <SelectItem value="received">Received</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-sm">{wo.orderedDate || '-'}</td>
                      <td className="px-4 py-3 text-sm">{wo.targetEnd}</td>
                      <td className="px-4 py-3 text-sm text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenEdit(wo)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleOpenDelete(wo)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Work Order Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingOrder ? 'Edit Work Order' : 'Create Work Order'}</DialogTitle>
            <DialogDescription>
              {editingOrder
                ? `Update work order ${editingOrder.woNumber}`
                : 'Create a new work order for production.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="poNumber" className="text-right">
                PO Number
              </Label>
              <Input
                id="poNumber"
                type="text"
                placeholder="PO-XXXX"
                value={formPoNumber}
                onChange={(e) => setFormPoNumber(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="product" className="text-right">
                Product
              </Label>
              <Select value={formProduct} onValueChange={setFormProduct}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {productOptions.map((product) => (
                    <SelectItem key={product} value={product}>
                      {product}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="qty" className="text-right">
                Quantity
              </Label>
              <Input
                id="qty"
                type="number"
                min="1"
                value={formQty}
                onChange={(e) => setFormQty(Number(e.target.value))}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="price" className="text-right">
                Price per Unit
              </Label>
              <Input
                id="price"
                type="number"
                min="0"
                step="0.01"
                value={formPricePerUnit}
                onChange={(e) => setFormPricePerUnit(Number(e.target.value))}
                className="col-span-3"
              />
            </div>

            {/* Calculated totals */}
            <div className="border-t pt-4 space-y-2">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right text-muted-foreground">
                  Subtotal
                </Label>
                <div className="col-span-3 font-medium">
                  ₪{subtotal.toFixed(2)}
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right text-muted-foreground">
                  VAT (18%)
                </Label>
                <div className="col-span-3 font-medium">
                  ₪{vat.toFixed(2)}
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right font-semibold">
                  Total
                </Label>
                <div className="col-span-3 font-bold text-lg text-primary">
                  ₪{total.toFixed(2)}
                </div>
              </div>
            </div>

            <div className="border-t pt-4 space-y-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="orderedDate" className="text-right">
                  Ordered Date
                </Label>
                <Input
                  id="orderedDate"
                  type="date"
                  value={formOrderedDate}
                  onChange={(e) => setFormOrderedDate(e.target.value)}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="targetEnd" className="text-right">
                  Target Date
                </Label>
                <Input
                  id="targetEnd"
                  type="date"
                  value={formTargetEnd}
                  onChange={(e) => setFormTargetEnd(e.target.value)}
                  className="col-span-3"
                />
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">
                Status
              </Label>
              <Select value={formStatus} onValueChange={setFormStatus}>
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusColumns.map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitWorkOrder}
              disabled={!formProduct || !formTargetEnd || formQty < 1}
            >
              {editingOrder ? 'Update' : 'Create'} Work Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Work Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete work order {orderToDelete?.woNumber}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
